'use client';

import { useEffect, useRef, useState } from 'react';
import {
  fetchBestBuyStores,
  type BestBuyEnvironment,
  type BestBuyFail,
  type BestBuyPickupStore,
} from './api';
import {
  BB_PICKUP_GTM_EVENTS,
  BB_PICKUP_ZIP_STORAGE_KEY,
  US_ZIP_RE,
} from './config';
import { sampleTag, trackPickupEvent } from './pickupAnalytics';
import type { BestBuyCheckTrigger } from './useBestBuyPickup';
import {
  clearPickupStore,
  getPickupSelection,
  refreshPickupChoice,
} from './pickupSelection';

export type BuyPagePickupStores =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'results';
      stores: BestBuyPickupStore[];
      environment: BestBuyEnvironment;
    }
  | { status: 'unavailable' }
  | { status: 'error'; failure: BestBuyFail };

const sanitizeZip = (raw: string): string => raw.replace(/\D/g, '').slice(0, 5);

const readStoredZip = (): string => {
  try {
    const stored = sessionStorage.getItem(BB_PICKUP_ZIP_STORAGE_KEY);
    return stored && US_ZIP_RE.test(stored) ? stored : '';
  } catch {
    return '';
  }
};

/** ZIP field and /stores lookup for the buy-page picker. Runs only while
 * `active` (pickup chosen and a SKU exists). Colour or size changes change
 * `sku`, which refetches: stock and tokens are per SKU. Tokens are never
 * kept; the hold step fetches a fresh one. */
export function useBuyPagePickup(args: {
  sku: string | null;
  active: boolean;
}) {
  const { sku, active } = args;
  const [zip, setZipState] = useState('');
  const [stores, setStores] = useState<BuyPagePickupStores>({ status: 'idle' });
  const [retry, setRetry] = useState(0);

  // Where the current ZIP came from, for the CHECK event's `trigger`: the
  // same vocabulary as the home hero (useBestBuyPickup.ts).
  const zipSourceRef = useRef<BestBuyCheckTrigger>('typed');

  // First time pickup opens: prefill from the stored choice or ZIP.
  useEffect(() => {
    if (!active) return;
    setZipState((current) => {
      if (current) return current;
      const stored = getPickupSelection().store?.zip || readStoredZip();
      if (stored) zipSourceRef.current = 'stored_zip';
      return stored;
    });
  }, [active]);

  const validZip = US_ZIP_RE.test(zip) ? zip : null;
  // The last lookup reported as a CHECK, so a Strict Mode re-run of the
  // effect (same deps) does not count twice. Cleared when the lookup goes
  // idle, so coming back to pickup counts again.
  const checkedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !sku || !validZip) {
      checkedRef.current = null;
      setStores({ status: 'idle' });
      return;
    }
    const controller = new AbortController();
    setStores({ status: 'loading' });
    // Same keys as the home hero's CHECK and RESULT (useBestBuyPickup.ts),
    // with the buy-page entry. One CHECK per lookup, one RESULT per lookup
    // that finishes (an aborted one reports nothing).
    const lookupKey = `${sku}|${validZip}|${retry}`;
    const analytics = {
      entry: 'buy_page',
      sku_mode: 'exact',
      area_prefix: validZip.slice(0, 3),
      trigger: retry > 0 ? 'retry' : zipSourceRef.current,
      ...sampleTag(),
    } as const;
    if (checkedRef.current !== lookupKey) {
      checkedRef.current = lookupKey;
      trackPickupEvent(BB_PICKUP_GTM_EVENTS.CHECK, { ...analytics, sku });
    }
    fetchBestBuyStores({
      postalCode: validZip,
      items: [{ sku, quantity: 1 }],
      signal: controller.signal,
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        if (!result.ok) {
          setStores({ status: 'error', failure: result });
          trackPickupEvent(BB_PICKUP_GTM_EVENTS.RESULT, {
            ...analytics,
            available: false,
            reason: result.code,
          });
          return;
        }
        const { data } = result;
        const current = getPickupSelection().store;
        trackPickupEvent(BB_PICKUP_GTM_EVENTS.RESULT, {
          ...analytics,
          available: data.pickupAvailable && data.stores.length > 0,
          store_count: data.stores.length,
        });
        if (!data.pickupAvailable || data.stores.length === 0) {
          setStores({ status: 'unavailable' });
          if (current) clearPickupStore();
          return;
        }
        setStores({
          status: 'results',
          stores: data.stores,
          environment: data.environment,
        });
        // Keep a choice only while the same store still has this SKU.
        if (current) {
          const still = data.stores.find(
            (s) => s.location_id === current.location_id && s.allItemsAvailable,
          );
          if (still) {
            if (
              current.sku !== sku ||
              current.zip !== validZip ||
              current.pickup_date !== still.pickup_date
            ) {
              refreshPickupChoice({
                ...current,
                sku,
                zip: validZip,
                pickup_date: still.pickup_date,
              });
            }
          } else {
            clearPickupStore();
          }
        }
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError')
          return;
        if (controller.signal.aborted) return;
        setStores({
          status: 'error',
          failure: {
            ok: false,
            reason: 'network',
            status: 0,
            code: 'network',
            message: '',
          },
        });
        trackPickupEvent(BB_PICKUP_GTM_EVENTS.RESULT, {
          ...analytics,
          available: false,
          reason: 'network',
        });
      });
    return () => controller.abort();
  }, [active, sku, validZip, retry]);

  return {
    zip,
    validZip,
    setZip: (value: string) => {
      zipSourceRef.current = 'typed';
      setZipState(sanitizeZip(value));
    },
    stores,
    retry: () => setRetry((n) => n + 1),
  };
}
